import { useMemo } from 'react';
import { useIntl } from 'react-intl';

import { type OrderingLedger } from '../../machine';
import { prepareLedger } from '../../utils';

export const usePaymentBreakdown = (params: UsePaymentBreakdownParams) => {
  const { amountEligibleForFlex, tip, availableCredit, shouldUseCredit, shouldUseFlex, ledger } =
    params;

  const { formatNumber } = useIntl();

  return useMemo(() => {
    const { totalWithoutCredit, amountPaidWithStripe, flex, credit } = prepareLedger({
      ledger,
      tip,
      availableCredit,
      shouldUseCredit,
      amountEligibleForFlex,
      shouldUseFlex,
    });

    return {
      totalAmount: totalWithoutCredit,
      flexAmount: flex,
      creditAmount: credit,
      stripeAmount: amountPaidWithStripe,

      formattedTotal: formatNumber(totalWithoutCredit / 100, {
        style: 'currency',
        currency: 'USD',
      }),
      formattedTotalFlex: formatNumber(negativeNumberIfNotZero(flex) / 100, {
        style: 'currency',
        currency: 'USD',
      }),
      formattedTotalCredit: formatNumber(negativeNumberIfNotZero(credit) / 100, {
        style: 'currency',
        currency: 'USD',
      }),
      formattedTotalStripe: formatNumber(amountPaidWithStripe / 100, {
        style: 'currency',
        currency: 'USD',
      }),
    };
  }, [
    formatNumber,
    ledger,
    tip,
    availableCredit,
    shouldUseCredit,
    shouldUseFlex,
    amountEligibleForFlex,
  ]);
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function negativeNumberIfNotZero(number: number) {
  return number > 0 ? -number : number;
}

// ─── Types ──────────────────────────────────────────────────────────────────

type UsePaymentBreakdownParams = {
  amountEligibleForFlex: number | null;
  tip: number;
  availableCredit: number;
  shouldUseCredit: boolean;
  shouldUseFlex: boolean;
  ledger?: OrderingLedger;
};
